import { useState } from "react";
import { Typography } from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";

import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";
import type { ActionItem } from "../../components/Menu";

export type StandardUserAction =
  | "view"
  | "edit"
  | "logs"
  | "deactivate"
  | "activate"
  | "deassociate";

type RenderCellTextOptions = {
  className?: string;
  title?: string;
};

/**
 * Renders truncated cell text for table display.
 *
 * Args:
 *   text: String content to render in the cell.
 *   className: Optional class name for styling.
 *   title: Optional title attribute for hover text.
 *
 * Returns:
 *   JSX.Element: Typography element with ellipsis styling.
 */
export const renderCellText = (
  text: string,
  { className, title }: RenderCellTextOptions = {}
) => (
  <Typography
    className={className}
    sx={{
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: "2",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }}
    title={title}
  >
    {text}
  </Typography>
);

/**
 * Builds action menu items for a user row.
 *
 * Args:
 *   values: Ordered list of action identifiers to include.
 *
 * Returns:
 *   ActionItem<T>[]: Menu items with labels and icons.
 */
export const buildUserActions = <T extends StandardUserAction>(
  values: readonly T[]
): ActionItem<T>[] =>
  values.map((value) => {
    switch (value) {
      case "view":
        return {
          label: "View",
          value,
          icon: <EyeIcon height={24} width={24} />,
        };
      case "edit":
        return {
          label: "Edit",
          value,
          icon: <EditIcon height={24} width={24} />,
        };
      case "logs":
        return {
          label: "Logs",
          value,
          icon: <img src={Logs} alt="Logs" width={24} />,
        };
      case "deactivate":
      case "deassociate":
        return {
          label: "Deactivate",
          value,
          icon: <DeactivateIcon height={24} width={24} />,
        };
      case "activate":
        return {
          label: "Activate",
          value,
          icon: <PowerSettingsNewIcon height={24} width={24} />,
        };
      default:
        return {
          label: "",
          value,
          icon: <span />,
        };
    }
  });

/**
 * Manages open/close state for confirmation, reason, and logs dialogs.
 *
 * Returns:
 *   object: Dialog state flags and open/close helpers.
 */
export const useStatusDialogs = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  return {
    confirmOpen,
    reasonOpen,
    logsOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    openReason: () => setReasonOpen(true),
    closeReason: () => setReasonOpen(false),
    openLogs: () => setLogsOpen(true),
    closeLogs: () => setLogsOpen(false),
    setReasonOpen,
    setConfirmOpen,
    setLogsOpen,
  };
};

/**
 * Clears the deactivate reason field and any related validation errors.
 *
 * Args:
 *   setValue: react-hook-form setter for field values.
 *   clearErrors: react-hook-form error resetter.
 */
/**
 * Clears the deactivate reason field and any related validation errors.
 *
 * Args:
 *   setValue: react-hook-form setter for field values.
 *   clearErrors: react-hook-form error resetter.
 *
 * Returns:
 *   void
 */
export const resetDeactivateReasonField = <TFieldValues extends FieldValues>(
  setValue: UseFormSetValue<TFieldValues>,
  clearErrors: UseFormClearErrors<TFieldValues>
) => {
  setValue(
    "deactivate_reason" as Path<TFieldValues>,
    "" as PathValue<TFieldValues, Path<TFieldValues>>
  );
  clearErrors("deactivate_reason" as Path<TFieldValues>);
};

/**
 * Normalizes a role label for matching.
 *
 * Args:
 *   roleName: Raw role label.
 *
 * Returns:
 *   string: Trimmed, lowercased role label.
 */
export const normalizeRoleName = (roleName: string) =>
  roleName.trim().toLowerCase();

/**
 * Determines whether the role uses laboratory/courier institution logic.
 *
 * Args:
 *   roleName: Role label to inspect.
 *
 * Returns:
 *   boolean: True when the role should use lab/courier institution bindings.
 */
export const isLabOrCourierRole = (roleName: string) => {
  const normalized = normalizeRoleName(roleName);
  return normalized.includes("lab") || normalized.includes("courier");
};

/**
 * Determines whether the role is a super admin.
 *
 * Args:
 *   roleName: Role label to inspect.
 *
 * Returns:
 *   boolean: True when the role is a super admin.
 */
export const isSuperAdminRole = (roleName: string) =>
  normalizeRoleName(roleName).includes("super admin");

/**
 * Type guard for UserRole objects from API responses.
 *
 * Args:
 *   value: Unknown value to validate.
 *
 * Returns:
 *   boolean: True when the value has a `name` field.
 */
export const isUserRole = (value: unknown): value is UserRole =>
  typeof value === "object" && value !== null && "name" in value;

/**
 * Type guard for role API services response payloads.
 *
 * Args:
 *   value: Unknown value to validate.
 *
 * Returns:
 *   boolean: True when the value has an optional `api_services` array.
 */
export const isRoleApiServicesResponse = <T,>(
  value: unknown
): value is { api_services?: T[] } => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const maybeResponse = value as { api_services?: unknown };
  return (
    maybeResponse.api_services === undefined ||
    Array.isArray(maybeResponse.api_services)
  );
};

/**
 * Resolves a user type from a role name string.
 *
 * Args:
 *   roleName: Role name from the API.
 *
 * Returns:
 *   "ADMIN" | "NURSE" | "COURIER" | undefined: Derived user type when matched.
 *
 * Notes:
 *   Returns undefined when the role is not recognized.
 */
export const resolveUserTypeFromRoleName = (roleName: string) => {
  const normalizedRoleName = normalizeRoleName(roleName);
  if (normalizedRoleName.includes("admin")) {
    return "ADMIN" as const;
  }
  if (normalizedRoleName.includes("nurse")) {
    return "NURSE" as const;
  }
  if (normalizedRoleName.includes("courier")) {
    return "COURIER" as const;
  }
  return undefined;
};
